import React, { useEffect, useRef } from 'react';
import { Popover } from '@headlessui/react';
import { capitalize } from 'lodash';
import Check from 'svg/Check';
import ChevronUp from 'svg/ChevronUp';
import classNames from 'styles/utils/classNames';
import { DropdownProps } from './types';

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  wrapperClassName,
  overlayClassName = 'flex flex-col gap-4',
  defaultOpen = false,
  shadowClassName = 'shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)]',
  buttonClassName,
  compact = false,
  openButtonClassName,
  chevronClassName = 'h-4 w-4',
  gapClassName,
  labelClassName,
  optionClassName,
  highlightSelected = false,
}) => {
  const selectedOption = options.find((option) => option.value === selectedValue);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultOpen && buttonRef.current) {
      buttonRef.current.click();
    }
  }, [defaultOpen]);

  return (
    <Popover className={classNames('relative', wrapperClassName)}>
      {({ open, close }) => (
        <>
          <Popover.Button
            ref={buttonRef}
            className={classNames(
              'flex w-full items-center justify-between gap-4 rounded-full border-color-border-hover-lightmode bg-color-bg-lightmode-primary px-4 py-3 dark:border-color-border-hover-darkmode dark:bg-color-bg-darkmode-primary',
              shadowClassName,
              buttonClassName,
              open && openButtonClassName,
            )}
          >
            <div
              className={classNames(
                'flex items-center',
                gapClassName ?? (compact ? 'gap-0.5' : 'gap-ds-xs'),
              )}
            >
              {selectedOption?.icon}
              <span
                key={selectedOption?.value}
                className={classNames(
                  'animate-word-enter whitespace-nowrap capitalize',
                  labelClassName ??
                    (compact
                      ? 'typography-product-subheading-mobile'
                      : 'typography-product-subheading'),
                )}
              >
                {selectedOption?.label ? capitalize(selectedOption.label) : ''}
              </span>
            </div>
            <ChevronUp
              className={classNames(
                'shrink-0 rotate-180 transition-transform',
                chevronClassName,
                open && '!rotate-0',
              )}
            />
          </Popover.Button>
          <Popover.Panel
            className={classNames(
              'absolute z-20 mt-2 w-full rounded-[1.5rem] border border-color-border-input-lightmode bg-color-bg-lightmode-primary dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary',
              highlightSelected ? 'p-ds-sm' : 'py-ds-lg',
              shadowClassName,
              overlayClassName,
            )}
          >
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onSelect(option.value);
                  close();
                }}
                className={classNames(
                  'flex items-center text-left transition-all',
                  highlightSelected
                    ? 'h-10 w-full rounded-full p-ds-sm'
                    : 'typography-product-button-label-medium border border-transparent px-ds-lg text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                  highlightSelected &&
                    option.value === selectedValue &&
                    'bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary',
                  gapClassName ?? (compact ? 'gap-0.5' : 'gap-ds-xs'),
                  optionClassName,
                )}
              >
                {option.icon}
                <span
                  className={classNames(
                    'whitespace-nowrap capitalize',
                    compact ? 'typography-product-subheading-mobile' : 'typography-product-subheading',
                    highlightSelected && option.value === selectedValue
                      ? 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
                      : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                  )}
                >
                  {capitalize(option.label)}
                </span>
                {!highlightSelected && option.value === selectedValue && (
                  <Check className="ml-auto h-5 w-5" />
                )}
              </button>
            ))}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};

export default Dropdown;
